import {
	Component,
	ComponentInterface,
	Element,
	h,
	Host,
	Listen,
	Prop,
	Method,
} from '@stencil/core';
import { Breakpoint, getCurrentBreakpoint } from '../../_helpers/breakpoints';
import type { VideoJsPlayer } from 'video.js';
import { Video } from '../video-player/video-player.interface';
import { dsConsole } from '../../_helpers/console';
import { getChildComponent } from '../../_helpers/prefixes';
import { debounce } from 'lodash';
import { removeInlineStylesFromHead } from '../../_helpers/content-security-policy';
import {
	startObserving,
	stopObserving,
} from '../../_helpers/moveInlineStylesToHeadObserver';

enum TranscriptPos {
	TranscriptContainer = 1,
	ContentContainer,
}

let observer;

/* In these breakpoints we will move the transcript up into the content container */
const transcriptRepositionBreakpoints = [Breakpoint.LG, Breakpoint.XL];

@Component({
	tag: 'ds-video-block',
})
export class VideoBlock implements ComponentInterface {
	@Element() el: HTMLDsVideoBlockElement;
	@Prop({ reflect: true }) includePlaylist = false;
	@Prop() dynamicTranscriptPositioning = true;

	videoPlayer: HTMLDsVideoPlayerElement;
	videoContentContainer: HTMLElement;
	videoContent: HTMLDsVideoContentElement;
	videoPlaylist: HTMLDsVideoPlaylistElement;
	videoTranscript: HTMLDsVideoTranscriptElement;
	videoJsInstance: VideoJsPlayer;

	previousBreakpoint: Breakpoint;
	newBreakpoint: Breakpoint;
	currentTranscriptPosition: TranscriptPos = TranscriptPos.TranscriptContainer;

	@Listen('resize', { target: 'window' })
	async windowResizeHandler() {
		if (!this.dynamicTranscriptPositioning) {
			return;
		}
		this.previousBreakpoint = this.previousBreakpoint || getCurrentBreakpoint();
		this.newBreakpoint = getCurrentBreakpoint();

		if (this.previousBreakpoint !== this.newBreakpoint) {
			const desiredPosition: TranscriptPos =
				transcriptRepositionBreakpoints.includes(this.newBreakpoint)
					? TranscriptPos.ContentContainer
					: TranscriptPos.TranscriptContainer;

			if (desiredPosition !== this.currentTranscriptPosition) {
				this.setTranscriptPosition(desiredPosition);
			}
		}
		this.debounceSetMinHeightOfVideoContent();
		this.previousBreakpoint = this.newBreakpoint;
	}

	@Method() async setMinHeightOfVideoContent() {
		if (
			transcriptRepositionBreakpoints.includes(getCurrentBreakpoint()) &&
			!this.el.getAttribute('appearance')?.includes('hero')
		) {
			this.videoContent.style.minHeight = `${
				this.videoPlayer.clientHeight - this.videoTranscript.clientHeight
			}px`;
		} else {
			this.videoContent.style.minHeight = 'auto';
		}
	}

	debounceSetMinHeightOfVideoContent = debounce(
		this.setMinHeightOfVideoContent,
		600
	);

	@Listen('playlistItemSelect')
	handlePlayListItemSelect(event: CustomEvent<Video>): void {
		this.videoContent.setVideo(event.detail);

		if (this.videoTranscript) {
			this.videoTranscript.updateText(event.detail.transcript);
		}
	}

	componentDidRender(): void {
		if (this.videoPlayer) {
			this.videoPlayer.getVideoEl().then(() => {
				this.setMinHeightOfVideoContent();
			});
		}
	}

	componentWillLoad() {
		this.videoPlayer = getChildComponent(this.el, 'ds-video-player');
		this.videoContentContainer = getChildComponent(
			this.el,
			'.ds-content-container'
		);
		this.videoContent = getChildComponent(this.el, 'ds-video-content');
		this.videoPlaylist = getChildComponent(this.el, 'ds-video-playlist');
		this.videoTranscript = getChildComponent(this.el, 'ds-video-transcript');

		if (!this.dynamicTranscriptPositioning) {
			dsConsole.warn(
				'Dynamic transcript positioning is disabled in video block.'
			);
		}

		if (!this.videoPlayer) {
			dsConsole.warn('Video block was unable to find video player component.');
			return;
		}

		if (
			this.dynamicTranscriptPositioning &&
			transcriptRepositionBreakpoints.includes(getCurrentBreakpoint())
		) {
			this.setTranscriptPosition(TranscriptPos.ContentContainer);
		}

		if (this.includePlaylist) {
			if (!this.videoPlaylist) {
				dsConsole.warn(
					'Video block was unable to find video playlist component.'
				);
				return;
			}
			this.initPlaylist();
		}

		if (window.MutationObserver) {
			startObserving(this.el, observer);
		}
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
		if (window.MutationObserver) {
			stopObserving(observer);
		}
	}

	initPlaylist() {
		this.videoPlayer.getVideoEl().then((videoEl) => {
			this.videoPlaylist.setVideoEl(videoEl);
		});
	}

	setTranscriptPosition(desiredPosition: TranscriptPos) {
		if (desiredPosition === TranscriptPos.ContentContainer) {
			if (this.videoContentContainer) {
				this.videoContentContainer.append(this.videoTranscript);
			} else {
				dsConsole.error(
					'Content container missing from ds-video-block inner HTML.'
				);
			}
		}
		if (desiredPosition === TranscriptPos.TranscriptContainer) {
			const transcriptContainer = this.el.querySelector(
				'.ds-transcript-container'
			);
			if (transcriptContainer) {
				transcriptContainer.append(this.videoTranscript);
			} else {
				dsConsole.error(
					'Transcript container missing from ds-video-block inner HTML.'
				);
			}
		}

		this.currentTranscriptPosition = desiredPosition;
	}

	render() {
		return <Host></Host>;
	}

	[memberName: string]: any;
}
